import React from 'react';
import { Check } from 'lucide-react';
import Input from '@/components/forms/Input';
import type {
    ActiveToggleProps,
    EquityInputProps,
    ExceptionToggleProps,
    HoursRangeProps,
    MinMaxRangeProps,
    RolesTableCellProps
} from './roleInterfaces';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue, } from "@/components/ui/select";
import HourInput, { formatMinutesToHourString } from './HourInput';

function ActiveToggle({ isEditing, config, canEdit, isException, onChange }: Readonly<ActiveToggleProps>) {
    const isApplied = config.active !== "false" && config.apply !== "false";
    if (isEditing) {
        return (
            <div className="flex items-center gap-2">
                <input
                    type="checkbox"
                    checked={isApplied}
                    disabled={!canEdit}
                    onChange={(e) => onChange(e.target.checked)}
                    className="h-4 w-4 accent-blue-600"
                />
                <span className={`text-sm ${isException ? 'text-red-600 font-bold' : ''}`}>Activé</span>
            </div>
        );
    }

    return (
        <span
            className={`${isApplied ? "text-gray-900" : "text-gray-400"} ${isException ? 'text-red-600 font-bold' : ''}`}>
            {isApplied ? "Activé" : "Désactivé"}
        </span>
    );
}

function HoursRange({ isEditing, config, canEdit, isException, onUpdateHours }: Readonly<HoursRangeProps>) {
    const startMinutes = config.start ?? 0;
    const endMinutes = config.end ?? 0;

    if (isEditing) {
        return (
            <div className="flex items-center gap-1 w-full">
                <div className="flex items-center w-full">
                    <HourInput
                        valueMinutes={startMinutes}
                        onChange={(val) => onUpdateHours(val.toString(), 'start')}
                        disabled={!canEdit}
                        className={`h-7 text-xs px-1 w-full text-center ${canEdit ? '' : 'bg-gray-50 text-gray-500'}`}
                    />
                </div>
                <span className="text-gray-400"> à </span>
                <div className="flex items-center w-full">
                    <HourInput
                        valueMinutes={endMinutes}
                        onChange={(val) => onUpdateHours(val.toString(), 'end')}
                        disabled={!canEdit}
                        className={`h-7 text-xs px-1 w-full text-center ${canEdit ? '' : 'bg-gray-50 text-gray-500'}`}
                    />
                </div>
            </div>
        );
    }

    return (
        <span className={isException ? 'text-red-600' : ''}>
            {formatMinutesToHourString(startMinutes)} à {formatMinutesToHourString(endMinutes)}
        </span>
    );
}


function MinMaxRange(
    {
        isEditing,
        config,
        canEdit,
        isException,
        onChangeMin,
        onChangeMax
    }: Readonly<MinMaxRangeProps>) {
    if (isEditing) {
        return (
            <div className="flex items-center gap-1 w-full">
                <Input
                    type="number"
                    value={config.min ?? 0}
                    disabled={!canEdit}
                    onChange={(e: React.ChangeEvent<HTMLInputElement>) => onChangeMin(Number.parseInt(e.target.value))}
                    className={`h-7 text-xs px-1 w-full text-center ${canEdit ? '' : 'bg-gray-50 text-gray-500'}`}
                />
                <span> vs </span>
                <Input
                    type="number"
                    value={config.max ?? 0}
                    disabled={!canEdit}
                    onChange={(e: React.ChangeEvent<HTMLInputElement>) => onChangeMax(Number.parseInt(e.target.value))}
                    className={`h-7 text-xs px-1 w-full text-center ${canEdit ? '' : 'bg-gray-50 text-gray-500'}`}
                />
            </div>
        );
    }

    return (
        <span className={isException ? 'text-red-600' : ''}>
            {config.min ?? 0} vs {config.max ?? 0}
        </span>
    );
}

function EquityInput(
    {
        isEditing,
        config,
        canEdit,
        isException,
        onChange,
    }: Readonly<EquityInputProps>) {

    const effEquity = config.equity || config.equity === 0 ? config.equity : 1;

    if (isEditing) {
        return (
            <div className="flex items-center gap-1 w-full">
                <Input
                    type="number"
                    value={effEquity}
                    disabled={!canEdit}
                    onChange={(e: React.ChangeEvent<HTMLInputElement>) => onChange(Number.parseInt(e.target.value))}
                    className={`h-7 text-xs px-1 w-full text-center ${canEdit ? '' : 'bg-gray-50 text-gray-500'}`}
                />
            </div>
        );
    }

    return (
        <span className={isException ? 'text-red-600' : ''}>
            {effEquity}
        </span>
    );
}

function ExceptionToggle({ isEditing, isException, onToggle }: Readonly<ExceptionToggleProps>) {
    if (isEditing) {
        return (
            <div className="flex items-center justify-center w-full cursor-pointer">
                <input
                    type="checkbox"
                    checked={isException}
                    onChange={onToggle}
                    className="h-4 w-4 accent-red-600"
                    aria-label="Exception"
                />
            </div>
        );
    }

    return isException && <Check size={16} className="text-red-600"/>;
}

export function HolidayStar() {
    return <span className="mr-1 text-amber-500" title="Férié" aria-label="Férié">★</span>;
}

export default function RolesTableCell(
    {
        day,
        config,
        isEditing,
        onConfigChange,
        hasDayOverride = false,
        skipHoliday = false,
        showActive = false,
        showNeedsIdeal = false,
        showQ = false,
        isRoleApplied = true,
        isHoliday = false
    }: Readonly<RolesTableCellProps>
) {
    const updateHours = (value: string, part: 'start' | 'end') => {
        const minutes = Number.parseInt(value, 10);

        if (!Number.isNaN(minutes)) {
            const field = part === 'start' ? 'start' : 'end';
            onConfigChange(day.dayIndex, field, minutes, hasDayOverride, day.date);
        }
    };

    const canEdit = isEditing;
    const isActive = config.active !== "false";
    const isApplied = isActive && config.apply !== "false";
    const showHolidaySkipLabel = true && skipHoliday && !isEditing;

    return (
        <td className={`border border-gray-300 align-top bg-white p-0 w-100 ${hasDayOverride ? 'border-red-200' : ''} ${isRoleApplied ? "" : "opacity-50 pointer-events-none grayscale"}`}>
            <div className="flex flex-col">
                <div className={`h-8 flex items-center justify-center  px-2 ${!isApplied ? "opacity-50 pointer-events-none grayscale" : ""}`}>

                    {isEditing ? (
                        <>
                            <Select
                                value={hasDayOverride ? "override" : "default"}
                                onValueChange={(val: string) => onConfigChange(day.dayIndex, 'active', 'TOGGLE_EXCEPTION', true, day.date)}
                            >
                                <SelectTrigger
                                    className="w-full h-6 text-xs text-center"
                                    style={{
                                        textAlign: "center",
                                        height: "28px",
                                        color: hasDayOverride ? "red" : "",
                                        fontWeight: hasDayOverride ? "700" : ""
                                    }}>
                                    <SelectValue placeholder="Mode"/>
                                </SelectTrigger>
                                <SelectContent>
                                    <SelectItem value="default">Défaut</SelectItem>
                                    <SelectItem value="override">Exception</SelectItem>
                                </SelectContent>
                            </Select>
                        </>
                    ) : (
                        <span
                            className={`text-xs ${hasDayOverride ? 'text-red-600 font-bold' : 'font-normal'}`}>
                            {isHoliday && <HolidayStar/>}{hasDayOverride ? "Exception" : "Défaut"}
                        </span>
                    )}
                </div>

                {showActive ? <div className="h-8 flex items-center justify-center border-b border-gray-100 px-2">
                    <ActiveToggle
                        isEditing={isEditing}
                        config={config}
                        canEdit={canEdit}
                        isException={hasDayOverride}
                        onChange={(checked) => onConfigChange(day.dayIndex, 'apply', checked ? "true" : "false", hasDayOverride, day.date)}
                    />
                </div> : ''}

                <div
                    className={`relative ${!isApplied ? "opacity-50 pointer-events-none grayscale" : ""}`}>
                    {showNeedsIdeal ?
                        <div className="h-8 flex items-center justify-center border-b border-gray-100 px-2">
                            <MinMaxRange
                                isEditing={isEditing}
                                config={config}
                                canEdit={canEdit}
                                isException={hasDayOverride}
                                onChangeMin={(value) => onConfigChange(day.dayIndex, 'min', value, hasDayOverride, day.date)}
                                onChangeMax={(value) => onConfigChange(day.dayIndex, 'max', value, hasDayOverride, day.date)}
                            />
                        </div> : ''}
                    <div className="h-8 flex items-center justify-center border-b border-gray-100 px-2">
                        <HoursRange
                            isEditing={isEditing}
                            config={config}
                            canEdit={canEdit}
                            isException={hasDayOverride}
                            onUpdateHours={updateHours}
                        />
                    </div>

                    {showQ ? <div className="h-8 flex items-center justify-center border-b border-gray-100 px-2">
                        <EquityInput
                            isEditing={isEditing}
                            config={config}
                            canEdit={canEdit}
                            isException={hasDayOverride}
                            onChange={(value) => onConfigChange(day.dayIndex, 'equity', value, hasDayOverride, day.date)}

                        />
                    </div> : ''}
                </div>
            </div>
        </td>
    );
}
